<template>
  <div class="pt-6 md:pt-0 relative overflow-x-auto">
    <Table
      :columns="tableColumns"
      :data="formattedData"
      is-updatable
      is-deletable
      @delete="deleteChallenge"
      @update="openChallengeUpdateModal"
    >
      <template #salesTargets="{ row }">
        <ul v-if="row.salesTargets.length" class="text-sm">
          <li v-for="salesTarget in row.salesTargets" :key="salesTarget.target">
            {{ t("sales_target", { target: salesTarget.target, reward: formatPrice(salesTarget.reward) }) }}
            <span v-if="salesTarget.label" class="text-gray-500">({{ salesTarget.label }})</span>
          </li>
        </ul>
        <span v-else class="text-sm">{{ t("points_per_euro", { points: row.pointsPerEuro }) }}</span>
      </template>
      <template #participants="{ row }">
        <div
          class="relative w-40 truncate cursor-pointer text-gray-600 flex items-center"
          @click="openParticipantsModal(row)"
        >
          <span class="truncate">{{ row.participants }}</span>
          <UserAddIcon v-if="canAddParticipants(row)" class="ml-1 h-5" />
        </div>
      </template>
      <template #rewardStatus="{ row }">
        <div class="flex">
          <div class="rounded-lg">
            <span
              :class="[
                'inline-block items-center px-3 py-0.5 mt-1 rounded-full text-xs font-medium',
                rewardStatusClasses(row.rewardStatus),
              ]"
            >
              {{ $t(`challenge.reward_status.${row.rewardStatus}`) }}
            </span>
          </div>
        </div>
      </template>
      <template #reporting="{ row }">
        <button @click="openReportingModal(row)" class="text-blue-600 hover:text-blue-800">
          <DownloadIcon class="cursor-pointerw-5 h-5" />
        </button>
      </template>
    </Table>
    <Modal :open="participantsModalOpen" @close="participantsModalOpen = false">
      <ChallengeParticipants
        v-if="selectedChallenge"
        class="p-2"
        :challenge="selectedChallenge"
        @challengeUpdated="challengeParticipantsUpdated"
      />
    </Modal>
    <Modal :open="reportingModalOpen" @close="reportingModalOpen = false">
      <ChallengeReportingForm v-if="selectedChallenge" class="p-2" :challenge="selectedChallenge" />
    </Modal>
    <Modal :open="challengeUpdateModalOpen" @close="challengeUpdateModalOpen = false">
      <ChallengeEditForm
        v-if="selectedChallenge"
        class="p-2"
        :challenge="selectedChallenge"
        @success="challengeUpdated"
      />
    </Modal>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { useI18n } from "vue-i18n";
import { Table, Modal, formatPrice, parseFrDate } from "@estaly/ui";
import { DownloadIcon, UserAddIcon } from "@heroicons/vue/outline";
import ChallengeEditForm from "@/components/challenge/ChallengeEditForm.vue";
import ChallengeReportingForm from "@/components/challenge/ChallengeReportingForm.vue";
import ChallengeParticipants from "@/components/challenge/ChallengeParticipants.vue";

export default {
  components: {
    Table,
    Modal,
    ChallengeEditForm,
    ChallengeReportingForm,
    ChallengeParticipants,
    DownloadIcon,
    UserAddIcon,
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  props: {
    challenges: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      participantsModalOpen: false,
      reportingModalOpen: false,
      selectedChallengeId: null,
      selectedStoreIds: [],
      challengeUpdateModalOpen: false,
    };
  },
  computed: {
    tableColumns() {
      return [
        { field: "id", label: "ID", bold: true },
        { field: "title", label: "Titre" },
        { field: "startDate", label: "Début" },
        { field: "endDate", label: "Fin" },
        { field: "rewardStatus", label: "Statut des gains", slot: "rewardStatus" },
        { field: "salesTargets", label: "Objectifs de vente", slot: "salesTargets" },
        { field: "participants", label: "Participants", slot: "participants" },
        { field: "reporting", label: "", slot: "reporting" },
      ];
    },
    formattedData() {
      return this.challenges.map((challenge) => ({
        id: challenge.id,
        title: challenge.title,
        startDate: challenge.startDate,
        endDate: challenge.endDate,
        rewardStatus: challenge.rewardStatus,
        salesTargets: challenge.salesTargets || [],
        pointsPerEuro: challenge.pointsPerEuro,
        participants: this.getFormattedOrganizations(challenge),
      }));
    },
    selectedChallenge() {
      return this.challenges.find((challenge) => challenge.id === this.selectedChallengeId);
    },
  },
  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("challenges", { removeChallenge: "delete" }),
    formatPrice,
    formatStartEndDates(challenge) {
      return `${challenge.startDate}${window.innerWidth < 768 ? " au " + challenge.endDate : ""}`;
    },
    getFormattedOrganizations(challenge) {
      return challenge.organizations.map((org) => org.name).join(" - ");
    },
    openChallengeUpdateModal(challenge) {
      this.selectedChallengeId = challenge.id;
      this.challengeUpdateModalOpen = true;
    },
    async deleteChallenge(challenge) {
      if (!confirm(`Supprimer le challenge ${challenge.title} (${challenge.id}) ?`)) return;

      const { success } = await this.removeChallenge(challenge.id);
      if (!success) return;

      await this.notify({
        category: "simple",
        type: "success",
        title: "Challenge supprimé",
      });
      this.$emit("refreshChallenges");
    },
    challengeUpdated() {
      this.challengeUpdateModalOpen = false;
      this.$emit("refreshChallenges");
    },
    challengeParticipantsUpdated() {
      this.$emit("refreshChallenges");
    },
    openParticipantsModal(row) {
      this.selectedChallengeId = row.id;
      this.participantsModalOpen = true;
    },
    openReportingModal(challenge) {
      this.selectedChallengeId = challenge.id;
      this.reportingModalOpen = true;
    },
    canAddParticipants(row) {
      return parseFrDate(row.startDate) > new Date();
    },
    rewardStatusClasses(status) {
      if (status == "activated") return "bg-green-100 text-green-800";
      if (status == "sent") return "bg-blue-100 text-blue-800";
      return "bg-yellow-100 text-yellow-800";
    },
  },
};
</script>

<i18n src="./ChallengeList.i18n.json"></i18n>
